<!-- HERO ------------------------------------------------------------------ -->
<section class="hero" aria-labelledby="hero-title">
  <picture class="hero__media">
    <img src="/assets/images/waste-water-treatment-plant.jpg"
         alt="Circular clarifier at a waste water treatment plant"
         width="1740" height="1160" fetchpriority="high" decoding="async">
  </picture>
  <div class="hero__scrim"></div>

  <div class="container hero__inner">
    <p class="eyebrow-pill" appReveal>
      <span class="dot" aria-hidden="true"></span> VASAI VIRAR · MAHARASHTRA
    </p>
    <h1 id="hero-title" class="hero__title" appReveal [revealDelay]="80">
      Waste water goes in. Clean water comes out. We build everything in between.
    </h1>
    <p class="hero__lede" appReveal [revealDelay]="160">
      Sewage and effluent treatment plants designed, manufactured, installed and commissioned
      in-house — plus a calibration lab for the instruments that keep them honest.
    </p>
    <div class="hero__actions" appReveal [revealDelay]="240">
      <a routerLink="/contact-us" class="btn btn--primary">Tell us about your water</a>
      <a routerLink="/equipment" class="btn btn--ghost">See the equipment</a>
    </div>
  </div>

  <span class="hero__cue" aria-hidden="true"><i></i></span>
</section>

<!-- STATS ----------------------------------------------------------------- -->
<section class="container stats-wrap" aria-label="Company at a glance">
  <div class="stats" appReveal>
    @for (s of stats; track s.label) {
    <div class="stat">
      <span class="stat__value" [appCountUp]="s.value" [suffix]="s.suffix">{{ s.value }}{{ s.suffix }}</span>
      <span class="stat__label">{{ s.label }}</span>
    </div>
    }
  </div>
</section>

<!-- PROCESS --------------------------------------------------------------- -->
<section id="process" class="container section" aria-labelledby="process-title">
  <header class="section__head" appReveal>
    <p class="eyebrow">FOLLOW THE WATER</p>
    <h2 id="process-title">Four stages. Every one of them is something we make.</h2>
  </header>

  <ol class="stages">
    @for (s of stages; track s.key; let i = $index) {
    <li class="stage card-lift" appReveal [revealDelay]="i * 80">
      <span class="stage__num">{{ i + 1 }}</span>
      <h3>{{ s.title }}</h3>
      <p>{{ s.blurb }}</p>
      <span class="stage__flow" aria-hidden="true"></span>
    </li>
    }
  </ol>
</section>

<!-- DIVISIONS ------------------------------------------------------------- -->
<section id="divisions" class="container section" aria-labelledby="divisions-title">
  <h2 id="divisions-title" class="visually-hidden">Our two divisions</h2>
  <div class="divisions">
    <article class="division division--water" appReveal>
      <img src="/assets/images/grey-water-treatment-plants1.jpg" alt="Grey water treatment plant"
           width="800" height="533" loading="lazy" decoding="async">
      <div class="division__body">
        <p class="eyebrow eyebrow--light">DIVISION 01</p>
        <h3>Waste water solutions</h3>
        <p>STP and ETP from 10 to 500 KLD — process design, fabrication, supply, installation
          and commissioning under one roof.</p>
        <ul class="chips"><li>MBBR</li><li>MBR</li><li>SBR</li><li>DAF</li></ul>
        <a routerLink="/equipment" class="link-arrow">Browse equipment</a>
      </div>
    </article>

    <article class="division division--lab" appReveal [revealDelay]="90">
      <img src="/assets/images/calibration-services.jpg" alt="Technician calibrating instruments in a laboratory"
           width="960" height="540" loading="lazy" decoding="async">
      <div class="division__body">
        <p class="eyebrow eyebrow--light">DIVISION 02</p>
        <h3>Calibration services</h3>
        <p>Temperature, electrical, dimensional, mass, air-flow and process instruments — in a
          controlled lab or on your site, to NABL procedure.</p>
        <ul class="chips"><li>On-site</li><li>HVAC validation</li><li>Temperature mapping</li></ul>
        <a routerLink="/contact-us" class="link-arrow">Ask about calibration</a>
      </div>
    </article>
  </div>
</section>

<!-- EQUIPMENT ------------------------------------------------------------- -->
<section id="equipment" class="container section" aria-labelledby="equipment-title">
  <header class="section__head section__head--split" appReveal>
    <div>
      <p class="eyebrow">WHAT WE MANUFACTURE</p>
      <h2 id="equipment-title">Fifteen equipment lines, all built in our own workshop.</h2>
    </div>
    <a routerLink="/equipment" class="link-arrow">See all equipment</a>
  </header>

  <ul class="eq-grid">
    @for (e of equipment; track e.slug; let i = $index) {
    <li class="eq card-lift" appReveal [revealDelay]="(i % 4) * 70">
      <a [routerLink]="['/equipment', e.slug]">
        <span class="eq__stage">{{ e.stage | uppercase }}</span>
        <h3>{{ e.name }}</h3>
        <p>{{ e.summary }}</p>
      </a>
    </li>
    }
  </ul>
</section>

<!-- PROJECTS -------------------------------------------------------------- -->
<section id="projects" class="container section" aria-labelledby="projects-title">
  <header class="section__head" appReveal>
    <p class="eyebrow">ON SITE</p>
    <h2 id="projects-title">Plants running in the field.</h2>
  </header>
  <div class="gallery">
    <figure class="shot" appReveal>
      <img src="/assets/images/dws-landustries-landy-adu-dhabi-750px.jpg" alt="Commissioned industrial effluent treatment plant"
           width="750" height="500" loading="lazy" decoding="async">
      <figcaption>Industrial ETP · commissioned</figcaption>
    </figure>
    <figure class="shot" appReveal [revealDelay]="80">
      <img src="/assets/images/water-treatment-plant-process-FEATURE-compressed.jpg" alt="Water treatment process line"
           width="1000" height="667" loading="lazy" decoding="async">
      <figcaption>Process line · 50 KLD MBBR</figcaption>
    </figure>
    <figure class="shot" appReveal [revealDelay]="160">
      <img src="/assets/images/grey-water-treatment-plants1.jpg" alt="Grey water reuse plant at a township"
           width="800" height="533" loading="lazy" decoding="async">
      <figcaption>Grey water reuse · township</figcaption>
    </figure>
  </div>
</section>

<!-- CTA ------------------------------------------------------------------- -->
<section class="cta" aria-labelledby="cta-title">
  <div class="container cta__inner">
    <div appReveal>
      <h2 id="cta-title">Tell us about your water.</h2>
      <p class="cta__lede">Two details — what you are treating and roughly how much per day —
        and we come back with a process train and an indicative budget.</p>
      <ul class="contact-list">
        <li><span aria-hidden="true">T</span>
          <a href="tel:+918983643660">+91 89836 43660</a><small>Mon–Sat, 9:30–7:00</small></li>
        <li><span aria-hidden="true">&#64;</span>
          <a href="mailto:info&#64;mechtronservices.com">info&#64;mechtronservices.com</a><small>Drawings and tender specs welcome</small></li>
        <li><span aria-hidden="true">&#9678;</span>
          <address>Shop No. 20, Ground Floor, Yashwant Deep, Agashi Road, near Central Park,
            Bolinj, Vasai Virar, Palghar, Maharashtra 401303</address></li>
      </ul>
    </div>
    <div class="cta__form" appReveal [revealDelay]="90">
      <a routerLink="/contact-us" class="btn btn--primary btn--block">Open the enquiry form</a>
      <a href="https://wa.me/918983643660" target="_blank" rel="noopener noreferrer"
         class="btn btn--ghost btn--block">WhatsApp instead</a>
    </div>
  </div>
</section>
